import { isNotDefined } from '@sgme/fp';
import { HStack } from '@sgme/ui';
import { type MouseEvent, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { sendHelpRequestThunk } from '@/api/help-request/index.ts';
import type { SendHelpRequestRequest } from '@/api/help-request/sendHelpRequest.ts';
import { SgTooltip } from '@/components/sg-design-system/components/feedback/SgTooltip';
import { logger } from '@/components/SgwtBootstrap/logger.ts';
import { getConfig } from '@/config/config.ts';
import { allFixingBulkOrdersSelector, allStopLossBulkOrdersSelector } from '@/store/bulkOrder/selectors.ts';
import {
  fixingOrderInvalid,
  fixingOrderStatusChanged,
  fixingOrderSuccess,
  resetBulkOrders,
  stopLossOrderInvalid,
  stopLossOrderStatusChanged,
  stopLossOrderSuccess,
} from '@/store/bulkOrder/slice.ts';
import {
  BulkOrderStatus,
  type FixingOrderError,
  type StopLossOrderError,
  type StopLossOrderFieldName,
} from '@/store/bulkOrder/state.ts';
import type { OrderError } from '@/store/bulkOrder/types.ts';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import { useSubmitFixingMutation } from '@/store/orders/fixing/api/index.ts';
import type { ValidationResponseDetailContent } from '@/store/orders/state.ts';
import { useSubmitStopLossMutation } from '@/store/orders/stopLoss/api/index.ts';
import { selectActiveTabId } from '@/store/tabs/slice.ts';

type ActionButtonsProps = {
  allStopLossBulkOrderIds: string[] | undefined;
  allFixingBulkOrderIds: string[] | undefined;
  isReadOnly?: boolean;
  stopLossExecutionStatus: BulkOrderStatus;
  fixingExecutionStatus: BulkOrderStatus;
  setStep: (step: number) => void;
  setHasErrorImporting: (hasErrorImporting: boolean) => void;
};

const ActionButtons = ({
  stopLossExecutionStatus,
  fixingExecutionStatus,
  setStep,
  setHasErrorImporting,
  allFixingBulkOrderIds,
  allStopLossBulkOrderIds,
  isReadOnly,
}: ActionButtonsProps) => {
  const dispatch = useAppDispatch();
  const [isFixingExecuted, setIsFixingExecuted] = useState(false);
  const [isStopLossExecuted, setIsStopLossExecuted] = useState(false);

  const [submitStopLoss] = useSubmitStopLossMutation();
  const [submitFixing] = useSubmitFixingMutation();

  const tabId = useAppSelector(selectActiveTabId);
  const allStopLossBulkOrders = useAppSelector((state) => allStopLossBulkOrdersSelector(state, tabId));
  const allFixingBulkOrders = useAppSelector((state) => allFixingBulkOrdersSelector(state, tabId));

  const executeStopLoss = async () => {
    if (isNotDefined(tabId)) {
      return;
    }

    allStopLossBulkOrders.forEach(async (order) => {
      dispatch(
        stopLossOrderStatusChanged({
          orderId: order.properties.id,
          status: BulkOrderStatus.PENDING,
          tabId,
        }),
      );
      const [currency1, currency2] = order.properties.currencyPair.split('/');

      if (isNotDefined(currency1) || isNotDefined(currency2) || isNotDefined(tabId)) {
        console.error('Invalid currency pair:', order.properties.currencyPair);
        return;
      }

      await submitStopLoss({
        tileId: order.properties.id,
        currency1: currency1,
        currency2: currency2,
        clientId: Number(order.properties.bdrId),
        way: order.properties.way === 'Ask' ? 'buy' : 'sell',
        notional: order.properties.notional,
        notionalCurrencyIndex: order.properties.currencyIndex,
        limitPrice: order.properties.limitPrice,
        expiry: order.properties.expiry,
        notificationEmails: order.properties.notificationEmails ?? [],
        trigger: order.properties.trigger,
        clockOffset: order.properties.clockOffset,
      })
        .unwrap()
        .then((response) => {
          if (response === undefined) {
            return;
          }

          response.type === 'success'
            ? dispatch(
                stopLossOrderSuccess({
                  orderId: order.properties.id,
                  omsId: response.orderId,
                  tabId,
                }),
              )
            : dispatch(
                stopLossOrderInvalid({
                  orderId: order.properties.id,
                  errors: extractErrorsFromValidation(response.errors) as StopLossOrderError[],
                  tabId,
                }),
              );
        })
        .catch(({ errors }: { type: 'failure'; errors: ValidationResponseDetailContent[] }) => {
          console.error('Error submitting stop loss order:', errors);
          dispatch(
            stopLossOrderStatusChanged({
              orderId: order.properties.id,
              status: BulkOrderStatus.FAILED,
              tabId,
            }),
          );

          if (errors.some((error) => error.errorDescription === 'timeout')) {
            const params: SendHelpRequestRequest = {
              config: 'orderpending',
              details: JSON.stringify({
                payload: errors,
                orderIdToUpdate: order.properties.id,
              }),
              data: {
                orderDescription: 'creation',
                orderType: 'limit',
                /**
                 *  send 'g10' or 'emerging' on order creation when we are able to differentiate
                 */
                orderCurrencyType: 'unknown',
                orderId: order.properties.id,
              },
              templateId: getConfig().app.mailTemplateIds.templateIdForPendingOrder,
              templateIdConfirmation: getConfig().app.mailTemplateIds.templateIdForPendingOrderConfirmation,
            };

            logger.logError('Order submission failed on timeout for {tileId_s}', order.properties.id);
            dispatch(sendHelpRequestThunk(params));
          }
        });
    });
  };

  const executeFixing = async () => {
    allFixingBulkOrders.forEach(async (order) => {
      const [currency1, currency2] = order.properties.currencyPair.split('/');

      if (isNotDefined(currency1) || isNotDefined(currency2) || isNotDefined(tabId)) {
        console.error('Invalid currency pair:', order.properties.currencyPair);
        return;
      }

      dispatch(
        fixingOrderStatusChanged({
          orderId: order.properties.id,
          status: BulkOrderStatus.PENDING,
          tabId,
        }),
      );

      await submitFixing({
        tileId: order.properties.id,
        currency1: currency1,
        currency2: currency2,
        clientId: Number(order.properties.bdrId),
        way: order.properties.way === 'Ask' ? 'buy' : 'sell',
        notional: order.properties.notional,
        notionalCurrencyIndex: order.properties.currencyIndex,

        fixingReference: order.properties.fixingReference,
        fixingDateUtc: order.properties.fixingDateUtc,
        expiryDay: order.properties.deliveryDate,
        fixingMarginType: order.properties.fixingMarginType,
        fixingPlace: order.properties.fixingPlace,
        fixingPriceType: order.properties.fixingPriceType,
        fixingTime: order.properties.fixingTime,
        commercialMargin: order.properties.commercialMargin,

        notificationEmails: order.properties.notificationEmails ?? [],
        clockOffset: order.properties.clockOffset,
      })
        .unwrap()
        .then((response) => {
          if (response === undefined) {
            return;
          }

          response.type === 'success'
            ? dispatch(
                fixingOrderSuccess({
                  orderId: order.properties.id,
                  omsId: response.orderId,
                  tabId,
                }),
              )
            : dispatch(
                fixingOrderInvalid({
                  orderId: order.properties.id,
                  errors: extractErrorsFromValidation(response.errors) as FixingOrderError[],
                  tabId,
                }),
              );
        })
        .catch((error) => {
          console.log('Error submitting fixing order:', error);

          console.error('Error submitting stop loss order:', error);
          dispatch(
            fixingOrderStatusChanged({
              orderId: order.properties.id,
              status: BulkOrderStatus.FAILED,
              tabId,
            }),
          );
        });
    });
  };

  // check if it's valid
  const canExecuteStopLoss =
    allStopLossBulkOrderIds && allStopLossBulkOrderIds.length > 0 && stopLossExecutionStatus === BulkOrderStatus.VALID;
  const canExecuteFixing =
    allFixingBulkOrderIds && allFixingBulkOrderIds.length > 0 && fixingExecutionStatus === BulkOrderStatus.VALID;

  const canExecuteAll = canExecuteStopLoss && canExecuteFixing;

  const onExecute = (event: MouseEvent<HTMLButtonElement>) => {
    // avoid to take the focus
    event.currentTarget.blur();
    executeFixing();
    executeStopLoss();
    setStep(3);
  };

  const onExecuteFixing = (event: MouseEvent<HTMLButtonElement>) => {
    event.currentTarget.blur();
    setIsFixingExecuted(true);
    executeFixing();
    if (
      (!isStopLossExecuted && (!allStopLossBulkOrderIds || allStopLossBulkOrderIds.length === 0)) ||
      isStopLossExecuted
    ) {
      setStep(3);
    }
  };

  const onExecuteStopLoss = (event: MouseEvent<HTMLButtonElement>) => {
    event.currentTarget.blur();
    setIsStopLossExecuted(true);
    executeStopLoss();

    if ((!isFixingExecuted && (!allFixingBulkOrderIds || allFixingBulkOrderIds.length === 0)) || isFixingExecuted) {
      setStep(3);
    }
  };

  const onReset = (event: MouseEvent<HTMLButtonElement>) => {
    // avoid to take the focus
    event.currentTarget.blur();
    dispatch(resetBulkOrders({ tabId }));
    setStep(1);
    setHasErrorImporting(false);
  };

  return (
    <HStack verticalAlignment="center" horizontalAlignment="end" gap="8px" className="mt-48px">
      <button
        type="button"
        className={'btn btn-md btn-icon-start btn-discreet-primary p-2'}
        onClick={onReset}
        data-e2e="reset-button"
      >
        {(stopLossExecutionStatus !== 5 || fixingExecutionStatus !== 5) && <span className="icon">restart_alt</span>}
        <FormattedMessage
          id={`bulkOrder.${stopLossExecutionStatus !== 5 || fixingExecutionStatus !== 5 ? 'button.reset' : 'button.done'}`}
        />
      </button>

      {canExecuteFixing && (
        <SgTooltip mode="hover" backgroundColor="black" color="white" placement="top" arrow>
          <SgTooltip.Trigger>
            <button
              type="button"
              className={`btn btn-md btn-icon-start ${canExecuteFixing ? 'btn-primary' : 'btn-outline-primary'}`}
              disabled={!canExecuteFixing || isReadOnly}
              onClick={onExecuteFixing}
              data-e2e="execute-button-fixing"
            >
              <span className="icon">done_all</span>
              <FormattedMessage id="bulkOrder.button.execute.fixing" />
            </button>
          </SgTooltip.Trigger>
          {isReadOnly && (
            <SgTooltip.Content className="p-2 text-center">
              <FormattedMessage id="bulkOrder.button.execute.readOnly.tooltip" />
            </SgTooltip.Content>
          )}
        </SgTooltip>
      )}

      {canExecuteStopLoss && (
        <SgTooltip mode="hover" backgroundColor="black" color="white" placement="top" arrow>
          <SgTooltip.Trigger>
            <button
              type="button"
              className={`btn btn-md btn-icon-start ${canExecuteStopLoss ? 'btn-primary' : 'btn-outline-primary'}`}
              disabled={!canExecuteStopLoss || isReadOnly}
              onClick={onExecuteStopLoss}
              data-e2e="execute-button-stop-loss"
            >
              <span className="icon">done_all</span>
              <FormattedMessage id="bulkOrder.button.execute.stopLoss" />
            </button>
          </SgTooltip.Trigger>
          {isReadOnly && (
            <SgTooltip.Content className="p-2 text-center">
              <FormattedMessage id="bulkOrder.button.execute.readOnly.tooltip" />
            </SgTooltip.Content>
          )}
        </SgTooltip>
      )}

      {canExecuteAll && (
        <SgTooltip mode="hover" backgroundColor="black" color="white" placement="top" arrow>
          <SgTooltip.Trigger>
            <button
              type="button"
              className={`btn btn-md btn-icon-start ${canExecuteAll ? 'btn-primary' : 'btn-outline-primary'}`}
              disabled={!canExecuteAll || isReadOnly}
              onClick={onExecute}
              data-e2e="execute-button-all"
            >
              <span className="icon">done_all</span>
              <FormattedMessage id="bulkOrder.button.execute" />
            </button>
          </SgTooltip.Trigger>
          {isReadOnly && (
            <SgTooltip.Content className="p-2 text-center">
              <FormattedMessage id="bulkOrder.button.execute.readOnly.tooltip" />
            </SgTooltip.Content>
          )}
        </SgTooltip>
      )}
    </HStack>
  );
};

export default ActionButtons;

export const extractErrorsFromValidation = (
  validationResponseDetailContent: ValidationResponseDetailContent[],
): OrderError[] => {
  return validationResponseDetailContent.map(
    (details: { field: string; errorDescription: string; errorCode: number }) =>
      ({
        field: details.field as StopLossOrderFieldName,
        message: details.errorDescription,
        code: details.errorCode,
      }) satisfies OrderError,
  );
};
